/**
 * LOT-UI-02 — Navigation primaire (5 entrées + Auto-Devis).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, ShoppingCart, Wrench, Package, Users, Lock, LogOut, PanelLeftClose, Bot, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getStaffHomeRoute, isPosOnlyUser, resolveMagasinRole } from '../../utils/roleRouting';

const AUTO_DEVIS_PATH = '/chat/devis-ia';

function showAutoDevisNav(user) {
  if (isPosOnlyUser(user)) return false;
  const mr = resolveMagasinRole(user);
  return mr !== 'technicien' && user?.role !== 'technicien';
}

function getPrimaryNavItems(user, t) {
  // Absent / suspension : uniquement le POS
  if (isPosOnlyUser(user)) {
    return [
      { key: 'vente', path: '/pos-v2', icon: ShoppingCart, label: t('nav.pos') || 'Vente', testId: 'nav-vente' },
    ];
  }
  const mr = resolveMagasinRole(user);
  const home = getStaffHomeRoute(user);
  const items = [
    { key: 'accueil', path: home, icon: LayoutDashboard, label: 'Accueil', testId: 'nav-accueil' },
  ];
  if (mr !== 'technicien') {
    items.push({ key: 'vente', path: '/pos', icon: ShoppingCart, label: t('nav.pos') || 'Vente', testId: 'nav-vente' });
  }
  items.push({ key: 'reparations', path: '/reparations', icon: Wrench, label: 'Réparations', testId: 'nav-reparations' });
  if (mr === 'super_admin' || mr === 'admin_magasin' || user?.role === 'admin') {
    items.push({ key: 'stock', path: '/stock', icon: Package, label: 'Stock', testId: 'nav-stock' });
  }
  items.push({ key: 'clients', path: '/clients', icon: Users, label: t('nav.clients') || 'Clients', testId: 'nav-clients' });
  return items;
}

const StaffPrimaryNav = ({ onLogout, onChangePassword, sidebarOpen, onClose, desktopCollapsed, onCollapse }) => {
  const { user, api } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const items = getPrimaryNavItems(user, t);
  const initial = user?.username ? user.username.charAt(0).toUpperCase() : 'U';
  const autoDevisVisible = showAutoDevisNav(user);
  const [pendingDevis, setPendingDevis] = useState(0);

  const loadPendingDevis = useCallback(async () => {
    if (!autoDevisVisible || !api) return;
    try {
      const res = await api.get('/chat-ai-devis?status=pending');
      setPendingDevis(res.data?.count || 0);
    } catch {
      /* ignore */
    }
  }, [api, autoDevisVisible]);

  useEffect(() => {
    loadPendingDevis();
    const iv = setInterval(loadPendingDevis, 60000);
    return () => clearInterval(iv);
  }, [loadPendingDevis]);

  const isActive = (path) => {
    if (path === AUTO_DEVIS_PATH) return location.pathname.startsWith(AUTO_DEVIS_PATH);
    if (path === '/pos' || path === '/pos-v2') {
      return location.pathname === '/pos' || location.pathname.startsWith('/pos-v2');
    }
    if (path === '/reparations') return location.pathname.startsWith('/reparations');
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <aside
      className={`remobiles-sidebar fixed top-16 left-0 w-[min(100vw-3rem,16rem)] sm:w-56 h-[calc(100vh-4rem)] z-40 overflow-y-auto transition-transform duration-300 ease-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } ${desktopCollapsed ? 'md:-translate-x-full' : 'md:translate-x-0'}`}
      data-testid="staff-primary-nav"
    >
      <div className="p-4 space-y-1 pb-24">
        <div className="p-3 rounded-xl mb-3 border border-white/10 bg-white/[0.03]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm bg-white/10">{initial}</div>
            <div className="min-w-0">
              <div className="font-bold text-sm truncate text-[var(--rm-white)]">{user?.username}</div>
              <div className="text-[10px] text-gray-500 truncate">
                {isPosOnlyUser(user) ? 'Absent · POS seul' : (user?.magasin_role || user?.role)}
              </div>
            </div>
          </div>
          <button type="button" onClick={onChangePassword}
            className="mt-2 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] font-medium border border-white/10 text-gray-400 hover:text-white hover:bg-white/5"
            data-testid="sidebar-change-password-btn">
            <Lock className="w-3 h-3" /> Mot de passe
          </button>
        </div>

        {items.map((item) => (
          <Link key={item.key} to={item.path} onClick={onClose}
            className={`flex items-center gap-3 px-3 py-3 min-h-[44px] rounded-xl text-sm transition-all ${isActive(item.path) ? 'border font-medium' : 'text-[var(--rm-gray-400)] hover:bg-white/5 hover:text-[var(--rm-white)] border border-transparent'}`}
            style={isActive(item.path) ? { background: 'var(--rm-red-muted)', color: 'var(--rm-red-light)', borderColor: 'var(--rm-border-strong)' } : {}}
            data-testid={item.testId}>
            <item.icon className="w-4 h-4" />
            <span>{item.label}</span>
          </Link>
        ))}

        {autoDevisVisible && (
          <Link
            to={AUTO_DEVIS_PATH}
            onClick={onClose}
            className={`nav-auto-devis relative flex items-center gap-3 px-3 py-3 min-h-[44px] rounded-xl text-sm font-semibold transition-all mt-2 ${
              isActive(AUTO_DEVIS_PATH)
                ? 'nav-auto-devis--active text-amber-100'
                : 'text-amber-200/90 hover:text-amber-50'
            }`}
            data-testid="nav-auto-devis"
          >
            <span className="nav-auto-devis-icon-wrap relative flex shrink-0 items-center justify-center w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-400/30">
              <Bot className="w-4 h-4 text-amber-300 nav-auto-devis-icon" />
              <Sparkles className="w-2.5 h-2.5 text-emerald-300 absolute -top-0.5 -right-0.5 nav-auto-devis-spark" />
            </span>
            <span className="flex-1">Auto-Devis</span>
            {pendingDevis > 0 && (
              <span className="nav-auto-devis-badge min-w-[1.25rem] h-5 px-1.5 rounded-full bg-amber-500 text-[10px] font-bold text-black flex items-center justify-center">
                {pendingDevis > 9 ? '9+' : pendingDevis}
              </span>
            )}
          </Link>
        )}

        <div className="border-t border-white/10 pt-3 mt-3 space-y-1">
          <button
            type="button"
            onClick={onCollapse}
            className="hidden md:flex w-full items-center gap-3 px-3 py-2.5 min-h-[40px] rounded-xl text-xs text-gray-400 hover:text-white hover:bg-white/5 transition-all"
            data-testid="sidebar-collapse-btn"
          >
            <PanelLeftClose className="w-4 h-4" />
            <span>Masquer le menu</span>
          </button>
          <button type="button" onClick={onLogout} className="w-full flex items-center gap-3 px-3 py-3 min-h-[44px] rounded-xl text-sm text-[var(--rm-red-light)] hover:bg-[var(--rm-red-muted)] transition-all" data-testid="logout-btn">
            <LogOut className="w-4 h-4" />
            <span>{t('nav.logout')}</span>
          </button>
        </div>
      </div>
    </aside>
  );
};

export { getPrimaryNavItems };
export default StaffPrimaryNav;
